import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import SearchToolbar from './SearchToolbar';
import SearchResults from './SearchResults';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as lookupSlice from '../../../data/lookup.slice';
import * as passengerSlice from '../../../data/passenger.slice';
import * as clientSlice from '../../../data/client.slice';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import PassengerProfileDialog from './PassengerProfileDialog';
import { usePortalName } from '../../../hooks/usePortalName';
import { usePortalClientBC } from '../../../hooks/usePortalClientBC';
import PageHeader from '../../controls/PageHeader';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { useAllowPassengerCreation } from '../../../hooks/useAllowPassengerCreation';
import { ClientState } from '@/data/models/client.model';
import { isPhoneSearchLongEnough } from '../../../services/phoneUtils';
import { isInIframe } from '../../../services/authService';

const PREFIX = 'Passengers';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(3),
  },
}));

export default function Passengers() {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const portalName = usePortalName();
  const portalClientBC = usePortalClientBC(portalName || '');

  const isSuperReaderUser = useIsSuperReaderUser();
  const allowPassengerCreation = useAllowPassengerCreation();

  const [searchParams] = useSearchParams();

  const clients = useAppSelector(clientSlice.selectClients);
  const genders = useAppSelector(lookupSlice.selectGenders);
  const authorizationReasons = useAppSelector(
    lookupSlice.selectPassengerAuthorizationReasons,
  );
  const searchBC = useAppSelector(passengerSlice.selectPassengerSearchClientBC);
  const searchText = useAppSelector(passengerSlice.selectPassengerSearchText);
  const phoneSearch = useAppSelector(passengerSlice.selectPassengerPhoneSearch);
  const searchResetToken = useAppSelector(
    passengerSlice.selectPassengerSearchResetToken,
  );
  const searchPage = useAppSelector(passengerSlice.selectPassengerSearchPage);
  const searchPageSize = useAppSelector(
    passengerSlice.selectPassengerSearchPageSize,
  );
  const loadingPassengers = useAppSelector(
    passengerSlice.selectLoadingPassengers,
  );
  const passengers = useAppSelector(passengerSlice.selectPassengers);
  const passengersLoaded = useAppSelector(
    passengerSlice.selectLoadedPassengers,
  );

  const [passengerDialogOpen, setPassengerDialogOpen] = React.useState(false);

  React.useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('passengerAuthorizationReason'));
    dispatch(lookupSlice.fetchLookupItemsAsync('gender'));
    dispatch(clientSlice.fetchClientsAsync());
  }, []);

  React.useEffect(() => {
    if (clients?.length) {
      const clientBC = searchParams.get('clientBC');
      const searchText = searchParams.get('searchTerm');
      if (clientBC) {
        onClientChange(clientBC);
      }
      if (searchText) {
        onSearchTextChange(searchText);
      }
    }
  }, [clients, searchParams]);

  React.useEffect(() => {
    const bc = portalClientBC || searchBC;
    const page = searchPage === passengers.currentPage ? 1 : searchPage;

    // The phone search wins when both are filled: it needs no client, and mixing the two is
    // rejected by the API anyway. Portals never set it - the field is not rendered there.
    if (phoneSearch) {
      if (isPhoneSearchLongEnough(phoneSearch)) {
        dispatch(
          passengerSlice.fetchPassengersByPhoneAsync(
            phoneSearch,
            page,
            searchPageSize,
          ),
        );
      } else if (passengersLoaded) {
        // Below the digit floor the API answers 400, so nothing is sent while the number is still
        // being typed. The previous page goes, rather than standing next to a number it does not
        // belong to.
        dispatch(passengerSlice.clearPassengers());
      }
    } else if (bc && searchText) {
      dispatch(
        passengerSlice.fetchPassengersAsync(
          bc,
          searchText,
          page,
          searchPageSize,
        ),
      );
    } else if (passengersLoaded) {
      dispatch(passengerSlice.clearPassengers());
    }
  }, [searchBC, searchText, phoneSearch, searchPage, searchPageSize]);

  const filteredClients = useMemo(() => {
    let result = clients;

    // only show active clients. Per https://americanlogistics.atlassian.net/browse/F1V2-5809
    result = result.filter(
      ({ state }) => state?.toLocaleLowerCase() === ClientState.active,
    );

    return result;
  }, [clients]);

  // The phone lookup is for an agent taking a call, so it is only offered where a call can reach
  // us: inside the Amazon Connect workspace iframe. Outside it there is no incoming number, and a
  // search across every client has no reason to be on screen. Portals are excluded as well - a
  // client must never see another client's passengers.
  const showPhoneSearch = !portalName && isInIframe();

  const onClientChange = (val: string | null) => {
    // A client and a phone lookup exclude each other: the phone search deliberately spans all
    // clients, and the API rejects the two together. Touching the client picker therefore drops
    // any phone term, which is also what keeps the effect below from firing a phone search that
    // the user has already navigated away from.
    dispatch(passengerSlice.setPassengerPhoneSearch(''));
    dispatch(passengerSlice.setPassengerSearchClientBC(val || ''));
  };

  const onSearchTextChange = (val: string) => {
    dispatch(passengerSlice.setPassengerSearchText(val));
  };

  const onPhoneSearchChange = (val: string) => {
    dispatch(passengerSlice.setPassengerPhoneSearch(val));
  };

  const onPageChange = (page: number) => {
    dispatch(passengerSlice.setPassengerSearchPage(page));
  };

  const onPageSizeChange = (pageSize: number) => {
    dispatch(passengerSlice.setPassengerSearchPageSize(pageSize));
  };

  const onCreatePassenger = () => {
    setPassengerDialogOpen(true);
  };

  const closePassengerProfileDialog = () => {
    dispatch(passengerSlice.setCurrentPassenger(null));
    setPassengerDialogOpen(false);
  };

  return (
    <StyledBox className={classes.container}>
      <PageHeader
        title={t('passenger.searchTitle')}
        primaryActionText={
          !isSuperReaderUser && allowPassengerCreation
            ? t('passenger.createPassenger')
            : undefined
        }
        onPrimaryAction={
          !isSuperReaderUser && allowPassengerCreation
            ? onCreatePassenger
            : undefined
        }
      />

      <SearchToolbar
        clients={filteredClients}
        currentClientBC={portalClientBC || searchBC}
        currentSearchText={searchText}
        currentPhoneSearch={phoneSearch}
        isPortal={!!portalName}
        showPhoneSearch={showPhoneSearch}
        searchResetToken={searchResetToken}
        onClientChange={onClientChange}
        onSearchTextChange={onSearchTextChange}
        onPhoneSearchChange={onPhoneSearchChange}
      />
      <SearchResults
        itemList={passengers}
        pageSize={searchPageSize}
        loading={loadingPassengers}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
      <PassengerProfileDialog
        title={t('passenger.createProfile')}
        portalClientBC={portalClientBC || undefined}
        clients={filteredClients}
        genders={genders}
        passengerAuthorizationReasons={authorizationReasons}
        open={passengerDialogOpen}
        close={closePassengerProfileDialog}
      />
    </StyledBox>
  );
}
